import * as math from 'mathjs';
import { MathScope } from 'mathjs';
import { Subject } from 'rxjs';
import { map,startWith } from 'rxjs/operators';
import { financialRound } from '../dave-data-module/entities/accounts-receivable-ledger.entity';
import { isNotNullOrUndefined,uniqArray } from './helper';


export type variableGroup = {
    description: string;
    id: number;
    children: variableGroup[];
};
export class CustomScope {
    protected localScope: Map<string, number>;
    protected groups: variableGroup[];
    protected meta: Map<
        string,
        {
            description: string;
            formula?: string;
            groupId: number;
        }
    >;
    protected variableTree: Map<string, string[]>;

    varChange$ = new Subject<string[]>();
    public errorHandler: (title: string, content: string) => void;
    constructor() {
        this.localScope = new Map();
        this.groups = [];
        this.meta = new Map();
        this.variableTree = new Map();
    }

    get(key: string) {
        // Remember to sanitize your inputs, or use
        // a datastructure that isn't a footgun.
        return this.localScope.get(key);
    }

    setAsFormula(key: string, value: string, meta?: { description: string; groupId: number }) {
        this.variableTree.set(key, getUsedVariableNames(value));

        const v = evaluateFormula(value, this);
        this.setMeta(key, { formula: value, description: meta.description || '', groupId: meta.groupId || null });
        this.set(key, v.res);
        return this;
    }
    set(key: string, value: number) {
        // todo betroffene Variablen neu berechnen
        if (value !== this.localScope.get(key)) {
            this.localScope.set(key, value);
        }
        const updated = this.recalculateAffected(key);
        this.varChange$.next(updated);
        console.log('set', key, value);
        return this;
    }

    recalculateAffected(key: string) {
        const toRecalculate: string[][] = [];

        const collect = (key: string, path: string[]) => {
            path.push(key);
            let pathIsEnd = true;
            this.variableTree.forEach((v, k) => {
                if (this.variableTree.get(k)?.includes(key)) {
                    pathIsEnd = false;
                    if (path.includes(k)) {
                        if (this.errorHandler) {
                            this.errorHandler('Parameter berechnen fehlgeschlagen', 'Zyklische Abhängigkeit gefunden: ' + path.join(' -> ') + ' -> ' + k);
                        } else {
                            throw new Error(`Cyclic dependency detected for variable ${k}, path: ${path.join(' -> ')} -> ${k}`);
                        }
                        return;
                    }
                    // toRecalculate.push(k);
                    collect(k, [...path]);
                }
            });
            if (pathIsEnd) {
                toRecalculate.push(path);
            }
        };
        // const changedVariables: string[] = [];
        const calculate = (key: string) => {
            const { res } = evaluateFormula(this.getMeta(key).formula, this);
            console.log('calculated', key, res);
            // this.set(key, res);
            if (this.localScope.get(key) !== res) {
                this.localScope.set(key, res);
                // changedVariables.push(key);
            }

            this.variableTree.forEach((v, k) => {
                if (this.variableTree.get(k)?.includes(key)) {
                    calculate(k);
                }
            });
        };
        collect(key, []);
        console.log('toRecalculate', toRecalculate);
        toRecalculate
            .flat()
            .filter((k) => k !== key)
            .forEach((k) => calculate(k));
        return uniqArray(toRecalculate.flat());
    }

    has(key: string) {
        return this.localScope.has(key);
    }

    keys() {
        return this.localScope.keys();
    }

    getMeta(key: string) {
        return this.meta.get(key) || { description: '', groupId: null };
    }
    setMeta(key: string, value: { description: string; formula?: string; groupId: number }) {
        this.meta.set(key, value);
        this.varChange$.next([key]);
    }
    get groupGetAll() {
        return this.groups;
    }
    groupGet(id: number) {
        return _groupChildGet(this.groups, id);
    }
    groupGetParent(id: number) {
        return _groupParentGet(this.groups, id);
    }
    groupAdd(description: string, parentId: number | null = null, index: number | null = null) {
        const usedIds = _groupGetIds(this.groups);
        let freeId = null;

        let i = 0;
        while (freeId === null) {
            i++;
            if (!usedIds.includes(i)) {
                freeId = i;
            }
        }
        if (parentId === null) {
            this.groups.splice(index || this.groups.length, 0, { description, id: freeId, children: [] });
        } else {
            const parent = this.groupGet(parentId);
            if (parent) {
                parent.children.splice(index || this.groups.length, 0, { description, id: freeId, children: [] });
            } else {
                throw new Error(`Parent group with id ${parentId} not found`);
            }
        }
    }
    groupRemove(id: number) {
        const group = this.groupGet(id);
        if (group) {
            const isRoot = this.groups.some((g) => g.id === id);
            const arr = isRoot ? this.groups : this.groupGetParent(group.id).children;
            const index = arr.findIndex((g) => g.id === id);
            arr.splice(index, 1);
        } else {
            throw new Error(`Group with id ${id} not found`);
        }
    }
    groupEdit(id: number, description: string) {
        const group = this.groupGet(id);
        if (group) {
            group.description = description;
            return group;
        } else {
            throw new Error(`Group with id ${id} not found`);
        }
    }
    removeVariable(key: string) {
        console.log('removeVariable', key);
        this.localScope.delete(key);
        this.meta.delete(key);
        this.varChange$.next([key]);
    }

    serialize() {
        return JSON.stringify({
            scope: Object.fromEntries(this.localScope),
            groups: this.groups,
            meta: Object.fromEntries(this.meta),
            variableTree: Object.fromEntries(this.variableTree),
        });
    }
    static deserialize(serialized: string) {
        const { scope, groups, meta, variableTree } = JSON.parse(serialized);
        const customScope = new CustomScope();
        customScope.localScope = new Map(Object.entries(scope));
        customScope.groups = groups;
        customScope.meta = new Map(Object.entries(meta));
        customScope.variableTree = new Map(Object.entries(variableTree));
        return customScope;
    }

    variables$ = this.varChange$.pipe(
        startWith(null),
        map(() => Array.from(this.keys()).map((k) => ({ key: k, value: this.get(k), meta: this.getMeta(k) }))),
    );
}
export class AdvancedCustomScope extends CustomScope {
    private readonly parentScope: CustomScope;

    constructor(parent: CustomScope) {
        super();
        this.parentScope = parent;
    }

    get(key: string) {
        return this.localScope.get(key) ?? this.parentScope?.get(key);
    }

    has(key: string) {
        return this.localScope.has(key) ?? this.parentScope?.has(key);
    }

    keys() {
        if (this.parentScope) {
            return new Set([...this.localScope.keys(), ...this.parentScope.keys()]).values();
        } else {
            return this.localScope.keys();
        }
    }

    delete(key: string) {
        return this.localScope.delete(key);
    }

    clear() {
        return this.localScope.clear();
    }

    toString() {
        return this.localScope.toString();
    }
}


const commentRegex = new RegExp(/"(.*?)"/g);
const decimalSeparator = ',';
const argumentSeparator = ';';
const separatorsRegex = new RegExp('\\' + decimalSeparator + '|\\' + argumentSeparator, 'g');

export function evaluateFormula(v: string, scope?: MathScope): { res: number | null; invalid: boolean } {
    let res: any | null;
    let invalid = false;
    try {
        let formula = (v || '').replace(commentRegex, '');
        formula = formula.replace(separatorsRegex, function (match) {
            return match === decimalSeparator ? '.' : ',';
        });

        res = scope ? math.evaluate(formula, scope) : math.evaluate(formula);
    } catch (e) {
        invalid = true;
        return { res: null, invalid };
    }
    if (!isNotNullOrUndefined(res)) {
        res = null;
    } else if (isNaN(res) || res === Infinity || res === -Infinity) {
        invalid = true;
    }
    return { res, invalid,  };
}
export function getUsedVariableNames(v: string): string[] {

    let formula = (v || '').replace(commentRegex, '');
    formula = formula.replace(separatorsRegex, function (match) {
        return match === decimalSeparator ? '.' : ',';
    });
    let usedVariables: math.MathNode[];
    try {
        usedVariables = math.parse(formula).filter((n) => n.type === 'SymbolNode');
    } catch (e) {
        console.warn('Error parsing formula:', e);
        return [];
    }

    // console.log('usedVariables', usedVariables);
    // console.log('usedVariables', uniqArray(usedVariables.map((n) => n.toString())));

    return uniqArray(usedVariables.map((n) => n.toString()));
}
export function evaluate(v: string, maxDecimalPlaces: number, scope?: MathScope): { res: number | null; invalid: boolean; resRounded: number | null } {
    const ret = { ...evaluateFormula(v, scope), resRounded: null };
    console.log(ret);

    if (!ret.invalid) {
        if (ret.res && isNotNullOrUndefined(maxDecimalPlaces)) {
            try {
                ret.resRounded = financialRound(ret.res, maxDecimalPlaces);
            } catch (e) {
                console.warn('Error rounding result:', e);
                ret.resRounded = null;
            }
        } else {
            ret.resRounded = ret.res;
        }
    }
    return ret;
}


function _groupChildGet(groups: variableGroup[], id: number) {
    let match: variableGroup | undefined;
    groups.forEach(group => {
        if (group.id === id) {
            match = group;
            return;
        }
        if (group.children) {
            match = this._groupChildGet(group.children, id);
            if (match) {
                return;
            }
        }
    });
    return match;
}
function _groupParentGet(groups: variableGroup[], id: number) {
    let match: variableGroup | undefined;
    groups.forEach(group => {
        if (group.children.some(child => child.id === id)) {
            match = group;
            return;
        }
        if (group.children) {
            match = this._groupParentGet(group.children, id);
            if (match) {
                return;
            }
        }
    });
    return match;
}
function _groupGetIds(groups: variableGroup[]) {
    const ids: number[] = [];
    groups.forEach((group) => {
        ids.push(group.id);
        if (group.children?.length) {
            return ids.push(...this._groupGetIds(group.children));
        }
    });
    return ids;
}
